import { useCallback, useEffect, useState } from 'react';

export type Theme = 'dark' | 'light' | 'hearth';

const STORAGE_KEY = 'sd:theme';
// Хроніки Альбіону: світла тема первинна, темна — тепла умбра.
const DEFAULT: Theme = 'hearth';

// Перемикач циклить лише між світлою (hearth) та темною. 'light' (старий
// пергамент) лишається валідним типом для сумісності, але поза циклом.
const THEMES: Theme[] = ['hearth', 'dark'];

export function readTheme(): Theme {
  if (typeof window === 'undefined') return DEFAULT;
  try {
    const v = localStorage.getItem(STORAGE_KEY) as Theme | null;
    return v && THEMES.includes(v) ? v : DEFAULT;
  } catch { return DEFAULT; }
}

function applyTheme(t: Theme): void {
  if (typeof document === 'undefined') return;
  document.body.dataset.theme = t;
}

/**
 * Global theme — застосовується через `body[data-theme=...]`.
 * Слухає 'sd:theme-changed' (custom event) і `storage` (з інших табів).
 */
export function useTheme(): {
  theme: Theme;
  setTheme: (t: Theme) => void;
  cycle: () => void;
} {
  const [theme, setThemeState] = useState<Theme>(() => readTheme());

  // Sync to body + localStorage on change
  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* noop */ }
    window.dispatchEvent(new CustomEvent('sd:theme-changed'));
  }, [theme]);

  // Listen to changes from other places (e.g. other tabs)
  useEffect(() => {
    function sync(): void {
      const next = readTheme();
      setThemeState((curr) => (curr === next ? curr : next));
    }
    window.addEventListener('sd:theme-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('sd:theme-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const cycle = useCallback(() => {
    setThemeState((curr) => {
      const idx = THEMES.indexOf(curr);
      return THEMES[(idx + 1) % THEMES.length]!;
    });
  }, []);

  return { theme, setTheme, cycle };
}

/** Helpers — для CSS-классу .dhh/.dh-light на root-елементах */
export function themeClass(theme: Theme): string {
  if (theme === 'hearth') return 'dhh';
  if (theme === 'light') return 'dh-light';
  return '';
}
